import { useCallback, useContext, useEffect, useState } from 'react';
import { ArrowBack } from '@mui/icons-material';
import { useApplicationsService } from 'modules/products/services/hooks';
import { ApplicationRequestContext } from '../ApplicationRequest';
import SearchInput from 'app/components/SearchFilter/SearchInput/SearchInput';
import EmptyState from 'app/components/EmptyState/EmptyState';
import AwaitingComponent from '../ConsignedStep/Awaiting/Awaiting';
import SimulationComponent from './Simulation/Simulation';
import { StepContentButtonsArea } from '../styles';
import { StyledSpinnerWrapper } from './styles';
import { usePersonContext } from 'modules/customer/context';
import {
  BoxSection,
  ButtonDefault,
  ButtonPrimary,
  Heading3,
} from 'app/components';

export const enum FgtsSteps {
  EMPTY_STATE = 'EMPTY_STATE',
  SIMULATION = 'SIMULATION',
  LOADING = 'LOADING',
  AUTHORIZATION = 'AUTHORIZATION',
  ERROR = 'ERROR',
  NO_BALANCE = 'NO_BALANCE',
}

type IFgtsStepProps = {
  isCalcSimulation?: boolean;
};

const FgtsStep = ({ isCalcSimulation }: IFgtsStepProps) => {
  const { loadings, getConsignedList, cancelConsignedPolling } =
    useApplicationsService();
  const { getPersonByTaxId } = usePersonContext();
  const {
    onBack,
    onForward,
    setConsignedList,
    consignedList,
    product,
    fgtsPeriods,
    fgtsValidationErrors,
    setConsignedProduct,
    setConsignedConfig,
    consignedError,
    setConsignedError,
  } = useContext(ApplicationRequestContext);

  const [currentStep, setCurrentStep] = useState<FgtsSteps>(
    FgtsSteps.EMPTY_STATE,
  );

  const getConsignedData = useCallback(
    async (taxId: string) => {
      setConsignedList();

      if (product?.id) {
        const response = await getConsignedList(
          taxId,
          product?.id,
          setConsignedError,
        );

        if (response?.links) {
          setConsignedList(response);
          await getPersonByTaxId(taxId);

          const consignedLink = response.links[0];

          if (consignedLink) {
            setConsignedConfig(consignedLink);
          }

          const fgtsResponse = consignedLink?.products?.[0];

          if (fgtsResponse) {
            setConsignedProduct(fgtsResponse);
          }

          return setCurrentStep(FgtsSteps.SIMULATION);
        }
      }
      return setCurrentStep(FgtsSteps.ERROR);
    },
    [product],
  );

  const steps = {
    [FgtsSteps.EMPTY_STATE]: (
      <EmptyState
        subtitle="Informe um CPF válido para a consulta de saldo."
        title="A busca não encontrou nenhum resultado."
        hasMarginTop
      />
    ),
    [FgtsSteps.AUTHORIZATION]: (
      <EmptyState
        subtitle="Sem essa autorização não é possível prosseguir com a consulta."
        title="O CPF consultado não permitiu o acesso às parcelas do FGTS"
        hasMarginTop
        icon="secondary"
      />
    ),
    [FgtsSteps.ERROR]: (
      <EmptyState
        subtitle={
          consignedError ||
          'O CPF não está disponível para consulta de saldo neste produto.'
        }
        title={
          consignedError
            ? 'Houve um problema na consulta.'
            : 'CPF não possui vínculos ativos.'
        }
        hasMarginTop
      />
    ),
    [FgtsSteps.NO_BALANCE]: (
      <EmptyState
        subtitle={
          consignedError ??
          'O CPF não possui saldo disponível para consulta de saldo neste produto.'
        }
        title="O CPF não possui saldo disponível."
        hasMarginTop
      />
    ),
    [FgtsSteps.LOADING]: (
      <AwaitingComponent
        backStep={() => {
          cancelConsignedPolling();
          setCurrentStep(FgtsSteps.EMPTY_STATE);
        }}
        hideSubtitle={loadings.isConsignedLoading}
        dataprevMessage={
          loadings.isDataprevPolling
            ? 'Processando consulta na Dataprev.'
            : undefined
        }
      />
    ),
    [FgtsSteps.SIMULATION]: (
      <SimulationComponent
        backStep={() => setCurrentStep(FgtsSteps.NO_BALANCE)}
        showFgtsQueryCheckbox
      />
    ),
  };

  useEffect(() => {
    if (loadings.isConsignedLoading) {
      return setCurrentStep(FgtsSteps.LOADING);
    }

    if (!loadings.isConsignedLoading && consignedList) {
      return setCurrentStep(FgtsSteps.SIMULATION);
    }
  }, [loadings.isConsignedLoading, consignedList]);

  return (
    <BoxSection>
      <Heading3>Consulta de saldo</Heading3>
      <p style={{ marginBottom: '2rem' }}>
        O resultado da margem disponível pode variar até a efetivação da
        operação.
      </p>

      <SearchInput
        placeholder="Digite o CPF para consultar o saldo"
        textButton="Consultar saldo"
        searchHandler={getConsignedData}
      />

      <StyledSpinnerWrapper>{steps[currentStep]}</StyledSpinnerWrapper>

      <StepContentButtonsArea>
        {!isCalcSimulation && (
          <ButtonDefault margin={true} onClick={onBack}>
            <ArrowBack />
          </ButtonDefault>
        )}
        <ButtonPrimary
          disabled={!fgtsPeriods?.length || fgtsValidationErrors}
          onClick={onForward}
        >
          Avançar
        </ButtonPrimary>
      </StepContentButtonsArea>
    </BoxSection>
  );
};

export default FgtsStep;
